import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, ShieldCheck, Plus, Minus } from "lucide-react";

const PROPS = [
  {
    k: "Two months on us",
    v: "Join before 7 February 2027 and add your card with confidence. Nothing is charged before 7 April 2027 — two launch months are on us.",
  },
  {
    k: "One flat price. Inc GST.",
    v: "$38.50 a month, GST included. No commission on bookings, no per-seat fees, no surprises on the invoice.",
  },
  {
    k: "Your clients always book free",
    v: "FYLLR never charges the people sitting in your chair. The membership is partner-only — clients just discover your last-minute openings and book.",
  },
  {
    k: "No lock-in. Cancel anytime.",
    v: "Leave whenever it stops serving you. Cancel before your first charge and you pay nothing, full stop.",
  },
];

const FAQ = [
  {
    q: "When am I first charged?",
    a: "If you join before 7 February 2027, $0 leaves your account before 7 April 2027. After that it’s $38.50/month, GST inclusive.",
  },
  {
    q: "Is there any commission on bookings?",
    a: "No. FYLLR takes 0% of your service revenue. Clients pay you directly, in your salon — the booking value never touches FYLLR. The flat monthly membership is the only thing you ever pay us.",
  },
  {
    q: "What does my membership actually include?",
    a: "A partner dashboard to post last-minute openings and recurring sessions, your editorial venue profile on the marketplace, booking reminders to clients, and a content studio to feed the FYLLR socials.",
  },
  {
    q: "Who handles payment and cancellations?",
    a: "You do. FYLLR surfaces the opening and sends the client — they pay you directly at the appointment, and your own cancellation policy applies. FYLLR never holds funds or manages refunds.",
  },
  {
    q: "How do I cancel?",
    a: "One tap from your dashboard. Cancel before your first charge and you're never billed. Cancel later and you simply won't renew the following month — no exit fees, no phone calls.",
  },
  {
    q: "Is my payment secure?",
    a: "Yes. Your membership card is captured and stored by Stripe, never by FYLLR — the same infrastructure that powers millions of businesses worldwide.",
  },
];

function FaqRow({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-bone/15" data-testid={`pricing-faq-${i}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-6 py-6 text-left group"
        data-testid={`pricing-faq-toggle-${i}`}
      >
        <span className="font-serif text-xl md:text-2xl text-bone leading-tight group-hover:text-mocha transition-colors">{q}</span>
        <span className="shrink-0 text-mocha">{open ? <Minus size={18} strokeWidth={1.5} /> : <Plus size={18} strokeWidth={1.5} />}</span>
      </button>
      {open && (
        <motion.p
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="pb-6 -mt-1 text-[15px] leading-relaxed text-bone/65 max-w-2xl"
        >
          {a}
        </motion.p>
      )}
    </div>
  );
}

export default function Pricing() {
  return (
    <div className="pt-14 md:pt-16 bg-espresso text-bone min-h-screen" data-testid="pricing-page">
      {/* HERO */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-[11px] tracking-[0.3em] uppercase text-mocha"
        >
          FYLLR for Partners
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 }}
          className="mt-5 font-serif text-5xl md:text-7xl leading-[0.95] text-bone max-w-3xl"
        >
          Join before launch.<br />Two months on us.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mt-6 text-base md:text-lg text-bone/65 max-w-xl leading-relaxed"
        >
          Join before 7 February 2027 to secure early access. Your first $38.50 payment is not due until 7 April 2027.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/salon/onboard"
            className="inline-flex items-center gap-2 rounded-full bg-bone text-ink px-7 py-3.5 text-sm hover:bg-sand transition-colors"
            data-testid="pricing-cta-join"
          >
            Start your free trial <ArrowRight size={15} strokeWidth={1.5} />
          </Link>
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-2 rounded-full border border-bone/30 px-7 py-3.5 text-sm text-bone/75 hover:border-mocha hover:text-bone transition-colors"
            data-testid="pricing-cta-marketplace"
          >
            See the marketplace
          </Link>
        </motion.div>
      </section>

      {/* PRICE CARD */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pb-16">
        <div className="relative overflow-hidden rounded-2xl border border-bone/15 bg-ink text-bone p-8 md:p-12" data-testid="pricing-plan-card">
          <div className="grid md:grid-cols-[1.1fr_1fr] gap-10 items-center">
            <div>
              <p className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-mocha">
                <ShieldCheck size={13} /> Partner membership
              </p>
              <h2 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95]">
                $38.50<span className="font-sans text-base text-bone/55"> AUD / month · inc GST</span>
              </h2>
              <p className="mt-4 text-[15px] text-bone/70 max-w-md leading-relaxed">
                Join before launch and pay nothing before 7 April 2027. Cancel anytime before your first charge and you pay nothing at all.
              </p>
              <Link
                to="/salon/onboard"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-bone text-ink px-7 py-3.5 text-sm hover:bg-sand transition-colors"
                data-testid="pricing-plan-cta"
              >
                Add card &amp; start free trial <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
            </div>
            <ul className="space-y-4">
              {[
                "Join before 7 February — $0 charged before 7 April 2027",
                "0% commission — every booking dollar is yours",
                "Clients always book free",
                "Unlimited last-minute openings & recurring sessions",
                "Editorial venue profile on the marketplace",
                "Clients pay you directly — cancellation on your terms",
                "Cancel anytime — no lock-in, no exit fees",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[15px] text-bone/80">
                  <Check size={16} className="text-mocha mt-0.5 shrink-0" strokeWidth={2} />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pb-20">
        <div className="grid md:grid-cols-2 gap-x-14 gap-y-12">
          {PROPS.map((p, i) => (
            <motion.div
              key={p.k}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: (i % 2) * 0.08 }}
              data-testid={`pricing-prop-${i}`}
            >
              <p className="font-serif text-2xl md:text-3xl text-bone leading-tight">{p.k}</p>
              <p className="mt-3 text-[15px] leading-relaxed text-bone/60 max-w-md">{p.v}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[900px] mx-auto px-6 md:px-10 pb-24">
        <h2 className="font-serif text-4xl md:text-5xl text-bone mb-8">Questions, answered.</h2>
        <div data-testid="pricing-faq">
          {FAQ.map((f, i) => (
            <FaqRow key={f.q} q={f.q} a={f.a} i={i} />
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-bone/15 bg-ink p-8 md:p-10 text-center">
          <h3 className="font-serif text-3xl md:text-4xl text-bone">Ready to fill the quiet hours?</h3>
          <p className="mt-3 text-[15px] text-bone/65 max-w-md mx-auto">
            Join before 7 February for two launch months free. Then $38.50/month, inc. GST.
          </p>
          <Link
            to="/salon/onboard"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-bone text-ink px-7 py-3.5 text-sm hover:bg-sand transition-colors"
            data-testid="pricing-cta-footer"
          >
            Start your free trial <ArrowRight size={15} strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
